// api/services/dashboardAnalytics.ts
import { handleRequest } from '@/api';
import { getChameleonHeaders } from '@/api/core/chameleonState';

type RequestData = Record<string, unknown>;

const withSignal = (data: RequestData, signal?: AbortSignal | null): RequestData =>
    signal ? { ...data, signal } : data;

export const getCustomerList = (signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('get', '/dashboard/customers', withSignal({}, signal));
};

export const getFiltersForCustomer = (customerId: number, signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('get', `/dashboard/filters/${customerId}`, withSignal({}, signal));
};

export const getImpressionSummary = (data: RequestData, signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('post', '/dashboard/impression_summary', withSignal(data, signal));
};

export const getImpressionsBeforeVisit = (data: RequestData, signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('post', '/dashboard/impressions_before_visit', withSignal(data, signal));
};

export const getResponsesByRegion = (data: RequestData, signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('post', '/dashboard/responses_by_region', withSignal(data, signal));
};

export const getResponsesBySite = (data: RequestData, signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('post', '/dashboard/responses_by_site', withSignal(data, signal));
};

export const getResponsesOverTime = (data: RequestData, signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('post', '/dashboard/responses_over_time', withSignal(data, signal));
};

export const getVisitResponseTiming = (data: RequestData, signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('post', '/dashboard/visit_response_timing', withSignal(data, signal));
};

export const getCreativePerformanceCost = (data: RequestData, signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('post', '/dashboard/creative_performance_cost', withSignal(data, signal));
};

export const getChannelPerformanceCost = (data: RequestData, signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('post', '/dashboard/channel_performance_cost', withSignal(data, signal));
};

/** Quiet on failure: the caller treats a missing booking cut as "not offered", not as an error */
export const getBookingPerformanceCost = (data: RequestData, signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('post', '/dashboard/booking_performance_cost', withSignal(data, signal), null, false);
};

export const getImpressionHeatmap = (data: RequestData, signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('post', '/dashboard/impression_heatmap', withSignal(data, signal));
};

export const getCampaignUplift = (data: RequestData, signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('post', '/dashboard/campaign_uplift', withSignal(data, signal));
};

export const getUniqueReach = (data: RequestData, signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('post', '/dashboard/unique_reach', withSignal(data, signal));
};

export const getReachBreakdown = (data: RequestData, signal?: AbortSignal | null): Promise<unknown> => {
    return handleRequest('post', '/dashboard/reach_breakdown', withSignal(data, signal));
};

/**
 * Streaming analysis via SSE. Calls the same endpoint but handles
 * text/event-stream responses with token-by-token delivery.
 * Falls back to standard JSON if the backend returns application/json.
 */
export const streamAnalysis = async (
    data: RequestData,
    onToken: (text: string) => void,
    onComplete: (result: unknown) => void,
    onError: (error: Error) => void,
    signal?: AbortSignal
): Promise<void> => {
    const baseUrl = import.meta.env.DEV
        ? import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
        : `https://${window.location.host}/api`;

    // Raw fetch bypasses the axios interceptor, so chameleon impersonation
    // headers must be attached manually — otherwise a super user in chameleon
    // mode appears to the backend as themselves and RBAC is short-circuited.
    const response = await fetch(`${baseUrl}/dashboard/analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getChameleonHeaders() },
        credentials: 'include',
        body: JSON.stringify(data),
        signal,
    });

    if (!response.ok) {
        throw new Error(`Analysis request failed: ${response.status}`);
    }

    // Non-streaming fallback (deterministic mode on backend)
    if (!response.headers.get('Content-Type')?.includes('text/event-stream')) {
        const result = await response.json();
        onComplete(result);
        return;
    }

    // SSE streaming path
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const events = buffer.split('\n\n');
            buffer = events.pop() || '';

            for (const event of events) {
                const lines = event.trim().split('\n');
                let eventType = '';
                let eventData = '';

                for (const line of lines) {
                    if (line.startsWith('event: ')) eventType = line.slice(7);
                    else if (line.startsWith('data: ')) eventData = line.slice(6);
                }

                if (!eventData) continue;

                try {
                    const parsed = JSON.parse(eventData);
                    if (eventType === 'token') {
                        onToken(parsed.text ?? parsed.content ?? eventData);
                    } else if (eventType === 'complete') {
                        // Handle both {result: {...}} and direct {...} formats
                        const result = parsed.result ?? parsed;
                        console.debug('Analysis complete event:', result);
                        onComplete(result);
                    } else if (eventType === 'error') {
                        onError(new Error(parsed.message || 'Analysis failed'));
                    }
                } catch {
                    // If JSON parse fails on a token event, pass raw text
                    if (eventType === 'token') {
                        onToken(eventData);
                    }
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
};
